import { TIconName } from '@smartshell/ui-kit';

import { ILogUserData } from 'schemas';

export interface IEventsFilter {
  q: string;
  types: Array<ELogType>;
  from?: string;
  to?: string;
}

export type TEventListItem = {
  id: number;
  type: ELogType;
  timestamp: string;
  description: string;
  host?: string;
  host2: {
    title: string;
  };
  subject: ILogUserData | null;
  object: ILogUserData | null;
  client: ILogUserData | null;
  role?: string;
  role2?: string;
  comment?: string;
  payment: {
    id: number;
    title: string;
    value: string;
  };
  value1: number;
  value2: number;
  penalty: number;
  booking: {
    cost: number;
    from: string;
    id: number;
    to: string;
  };
  hosts: {
    value: number;
    title: string;
    id: number;
  }[];
  tariff: {
    id: number;
    title: string;
  };
  cash_order: {
    type: 'PKO' | 'RKO';
    sum: number;
    comment: string;
  };
  client_session: {
    elapsed: number;
    expires_at: string;
    finished_at: string;
    id: number;
    time_left: number;
    is_per_minute: boolean;
    total_cost: number;
  };
  deposit: number;
  shortcut: {
    title: string;
  };
  duration: number;
  combo: {
    host_group_id: number;
    host_group_title: string;
    id: number;
    tariff_id: number;
    tariff_title: string;
    title: string;
  };
  combo_items: {
    title: number;
    price: number;
    amount: number;
  }[];
  discount: {
    title: string;
    value: number;
  };
  good: {
    title: string;
  };
  goods: {
    title: string;
  }[];
  host_group: {
    title: string;
  };
  new_devices: {
    alias: string;
    value: string;
    value2: string;
  }[];
  operator: {
    first_name: string;
    last_name: string;
  };
  cash_on_start: number;
  promo_code_item: {
    id: number;
    title: string;
    description: string;
    value: string;
    applicable_params: string;
    client_scope: string;
    scopes: string;
    active: boolean;
    active_from: string;
    active_to: string;
    is_tracked: boolean;
    created_at: string;
  };
  promo_campaign: {
    id: number;
    title: string;
    description: string;
    value: string;
    applicable_params: string;
    client_scope: string;
    scopes: string;
    generation_settings: string;
    counters: string;
    active: string;
    active_from: string;
    active_to: string;
    is_tracked: boolean;
    created_at: string;
    updated_at: string;
  };
  service: {
    title: string;
  };
  setting: {
    alias: string;
    value: string;
    value2: string;
  };
  user_group: {
    title: string;
    value: number;
  };
  user_group2: {
    title: string;
    value: number;
  };
  worker_task: {
    title: string;
  };
  host_command: {
    title: string;
  };
  cashback: number;
  payment_items: {
    amount: number;
    entity_type: string;
    sum: number;
    title: string;
    id: number;
  }[];
  payment_transaction: {
    payment_id: number;
    uuid: string;
  };
  warehouse_item: {
    value2: number;
    value: number;
    title: string;
    entity_type: string;
  };
  additional_seconds: number;
  send_code_method: string;
  goods_category: {
    title: string;
  };
  additional_data: string;
  company_news_item: {
    id: string;
    image: string;
    label: string;
    url: string;
    title: string;
    active: boolean;
    active_from: string;
    active_to: string;
    created_at: string;
  };
  company_news_item_changes: {
    id: string;
    image: string;
    label: string;
    url: string;
    title: string;
    active: boolean;
    active_from: string;
    active_to: string;
    created_at: string;
  };
  manager: {
    nickname: string;
    phone: string;
    first_name: string;
    last_name: string;
    uuid: string;
  };
  advertisement: {
    id: string;
    title: string;
  };
};

export enum ELogType {
  CLUB_CREATED = 'CLUB_CREATED',
  TELEGRAM_LINKED = 'TELEGRAM_LINKED',
  TELEGRAM_UNLINKED = 'TELEGRAM_UNLINKED',

  PAYMENT_CREATED = 'PAYMENT_CREATED',
  BONUS_PAYMENT_CREATED = 'BONUS_PAYMENT_CREATED',
  BONUS_UPDATED = 'BONUS_UPDATED',
  PAYMENT_REFUND = 'PAYMENT_REFUND',
  CASH_ORDER_CREATED = 'CASH_ORDER_CREATED',
  DEPOSIT_ADDED_ONLINE = 'DEPOSIT_ADDED_ONLINE',
  DEPOSIT_UPDATED = 'DEPOSIT_UPDATED',

  CLIENT_SESSION_STARTED = 'CLIENT_SESSION_STARTED',
  CLIENT_SESSION_PROLONGED = 'CLIENT_SESSION_PROLONGED',
  CLIENT_SESSION_FINISHED = 'CLIENT_SESSION_FINISHED',
  CLIENT_SESSION_HOST_CHANGED = 'CLIENT_SESSION_HOST_CHANGED',
  CLIENT_SESSION_PAUSED = 'CLIENT_SESSION_PAUSED',
  CLIENT_SESSION_UNPAUSED = 'CLIENT_SESSION_UNPAUSED',
  CLIENT_SESSION_UNPAUSED_AUTO = 'CLIENT_SESSION_UNPAUSED_AUTO',
  HOST_PUNISHED = 'HOST_PUNISHED',

  SEND_RESET_CODE = 'SEND_RESET_CODE',
  SEND_VERIFY_CODE = 'SEND_VERIFY_CODE',

  USER_RESET_PASSWORD = 'USER_RESET_PASSWORD',
  USER_REGISTERED = 'USER_REGISTERED',
  USER_BANNED = 'USER_BANNED',
  USER_UNBANNED = 'USER_UNBANNED',
  USER_DISCOUNT_UPDATED = 'USER_DISCOUNT_UPDATED',
  USER_VERIFIED = 'USER_VERIFIED',

  CLIENT_AUTHORIZED_FROM_HOST = 'CLIENT_AUTHORIZED_FROM_HOST',
  WORKER_AUTHORIZED_FROM_BILLING = 'WORKER_AUTHORIZED_FROM_BILLING',
  WORKER_AUTHORIZED_FROM_HOST = 'WORKER_AUTHORIZED_FROM_HOST',

  WORK_SHIFT_STARTED = 'WORK_SHIFT_STARTED',
  WORK_SHIFT_FINISHED = 'WORK_SHIFT_FINISHED',

  HOST_SESSION_STARTED = 'HOST_SESSION_STARTED',
  HOST_SESSION_FINISHED = 'HOST_SESSION_FINISHED',

  HOST_GROUP_CREATED = 'HOST_GROUP_CREATED',
  HOST_GROUP_DELETED = 'HOST_GROUP_DELETED',

  HOST_CREATED = 'HOST_CREATED',
  HOST_SWITCHED_GROUP = 'HOST_SWITCHED_GROUP',

  WORKER_CREATED = 'WORKER_CREATED',
  WORKER_UPDATED = 'WORKER_UPDATED',
  WORKER_DELETED = 'WORKER_DELETED',
  WORKER_ENABLED = 'WORKER_ENABLED',

  TARIFF_CREATED = 'TARIFF_CREATED',
  TARIFF_UPDATED = 'TARIFF_UPDATED',
  TARIFF_DELETED = 'TARIFF_DELETED',

  SERVICE_CREATED = 'SERVICE_CREATED',
  SERVICE_UPDATED = 'SERVICE_UPDATED',
  SERVICE_DELETED = 'SERVICE_DELETED',

  GOOD_CREATED = 'GOOD_CREATED',
  GOOD_UPDATED = 'GOOD_UPDATED',
  GOOD_DELETED = 'GOOD_DELETED',
  GOOD_LOW_STOCK_THRESHOLD_REACHED = 'GOOD_LOW_STOCK_THRESHOLD_REACHED',

  DISCOUNT_CREATED = 'DISCOUNT_CREATED',
  DISCOUNT_UPDATED = 'DISCOUNT_UPDATED',
  DISCOUNT_DELETED = 'DISCOUNT_DELETED',

  PROMO_CODE_CREATED = 'PROMO_CODE_CREATED',
  PROMO_CODE_UPDATED = 'PROMO_CODE_UPDATED',
  PROMO_CODE_DELETED = 'PROMO_CODE_DELETED',
  PROMO_CODE_USED = 'PROMO_CODE_USED',
  PROMO_CODE_EXPIRED = 'PROMO_CODE_EXPIRED',
  PROMO_CAMPAIGN_CREATED = 'PROMO_CAMPAIGN_CREATED',
  PROMO_CAMPAIGN_UPDATED = 'PROMO_CAMPAIGN_UPDATED',
  PROMO_CAMPAIGN_DELETED = 'PROMO_CAMPAIGN_DELETED',
  PROMO_CAMPAIGN_CODES_GENERATED = 'PROMO_CAMPAIGN_CODES_GENERATED',
  PROMO_CAMPAIGN_EXPIRED = 'PROMO_CAMPAIGN_EXPIRED',

  TASK_ADDED = 'TASK_ADDED',

  WAREHOUSE_GOODS_ADDED = 'WAREHOUSE_GOODS_ADDED',
  WAREHOUSE_GOODS_DISPOSED = 'WAREHOUSE_GOODS_DISPOSED',

  HOST_HARDWARE_CHANGED = 'HOST_HARDWARE_CHANGED',
  HOST_HARDWARE_APPLIED = 'HOST_HARDWARE_APPLIED',

  SHELL_DISABLED = 'SHELL_DISABLED',
  SHELL_ENABLED = 'SHELL_ENABLED',
  SHELL_HIGH_ACCESS_DISABLED = 'SHELL_HIGH_ACCESS_DISABLED',
  SHELL_HIGH_ACCESS_ENABLED = 'SHELL_HIGH_ACCESS_ENABLED',

  SEND_HOST_COMMAND = 'SEND_HOST_COMMAND',

  HOST_ADMIN_CALLED = 'HOST_ADMIN_CALLED',
  HOST_ADMIN_CALL_CHECKED = 'HOST_ADMIN_CALL_CHECKED',
  HOST_ADMIN_CALL_FAILED = 'HOST_ADMIN_CALL_FAILED',

  CLUB_COMMENT_CREATED = 'CLUB_COMMENT_CREATED',

  ACHIEVEMENT_CREATED = 'ACHIEVEMENT_CREATED',
  ACHIEVEMENT_DELETED = 'ACHIEVEMENT_DELETED',
  ACHIEVEMENT_ASSIGNED = 'ACHIEVEMENT_ASSIGNED',

  BOOKING_CREATED = 'BOOKING_CREATED',
  BOOKING_CANCELED = 'BOOKING_CANCELED',
  BOOKING_FAILED_TO_START = 'BOOKING_FAILED_TO_START',
  BOOKING_UPDATED = 'BOOKING_UPDATED',
  BOOKING_CONFIRMED = 'BOOKING_CONFIRMED',

  COMBO_CREATED = 'COMBO_CREATED',
  COMBO_UPDATED = 'COMBO_UPDATED',
  COMBO_DELETED = 'COMBO_DELETED',

  POST_PAYMENT_CANCELED = 'POST_PAYMENT_CANCELED',
  DEBT_ACTIVATED = 'DEBT_ACTIVATED',
  SETTINGS_UPDATED = 'SETTINGS_UPDATED',

  USER_GROUP_UPDATED = 'USER_GROUP_UPDATED',
  USER_GROUP_CREATED = 'USER_GROUP_CREATED',
  USER_GROUP_DELETED = 'USER_GROUP_DELETED',
  USER_CHANGED_GROUP = 'USER_CHANGED_GROUP',

  GOOD_CATEGORY_CREATED = 'GOOD_CATEGORY_CREATED',
  GOOD_CATEGORY_UPDATED = 'GOOD_CATEGORY_UPDATED',
  GOOD_CATEGORY_DELETED = 'GOOD_CATEGORY_DELETED',

  USER_DEPOSIT_TRANSFERRED = 'USER_DEPOSIT_TRANSFERRED',

  NEWS_CREATED = 'NEWS_CREATED',
  NEWS_DELETED = 'NEWS_DELETED',
  NEWS_UPDATED = 'NEWS_UPDATED',

  HOST_QR_GENERATE = 'HOST_QR_GENERATE',

  SHORTCUT_CREATED = 'SHORTCUT_CREATED',
  SHORTCUT_UPDATED = 'SHORTCUT_UPDATED',
  SHORTCUT_DELETED = 'SHORTCUT_DELETED',

  SETTINGS_COPIED = 'SETTINGS_COPIED',

  ADVERTISEMENT_MODERATION = 'ADVERTISEMENT_MODERATION',
  ADVERTISEMENT_APPROVED = 'ADVERTISEMENT_APPROVED',
  ADVERTISEMENT_REJECTED = 'ADVERTISEMENT_REJECTED',
  ADVERTISEMENT_MANUAL_COMPLETED = 'ADVERTISEMENT_MANUAL_COMPLETED',
}

export interface ILogConfig {
  title: string;
  iconName: TIconName;
  color: 'green' | 'orange' | 'blue' | 'red' | 'white' | 'gray';
}
